/**
 * RiskInsightCard Component
 *
 * Displays a market overview with S&P 500 data as a teaser for portfolio risk analysis.
 * Prompts user to upload their portfolio for personalized analysis.
 */

import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import type { InsightConfig } from '../quizTypes';
import type { FMPQuote } from '../../../services/fmpDataService';
import { typography } from '../../../common/styles/universalTheme';
import InsightCard from './InsightCard';

interface RiskInsightCardProps {
  config: InsightConfig;
  quote: FMPQuote | null; // SPY quote as market proxy
  isLoading: boolean;
  onNavigate: (route: string) => void;
}

const RiskInsightCard: React.FC<RiskInsightCardProps> = ({
  config,
  quote,
  isLoading,
  onNavigate,
}) => {
  const theme = useTheme();

  // Determine change color
  const changeColor =
    quote && quote.changesPercentage >= 0
      ? theme.palette.success.main
      : theme.palette.error.main;

  return (
    <InsightCard
      label={config.label}
      rationale={config.rationale}
      deepLinkRoute={config.deepLinkRoute}
      onNavigate={onNavigate}
      isLoading={isLoading}
    >
      {quote && (
        <Box>
          {/* Market index info */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
              }}
            >
              S&P 500
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {quote.symbol}
            </Typography>
          </Box>

          {/* Index level and change */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1 }}>
            <Typography
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontVariantNumeric: 'tabular-nums lining-nums',
                fontWeight: 600,
                fontSize: typography.fontSize.lg,
                color: 'text.primary',
              }}
            >
              {quote.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
            <Typography
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontVariantNumeric: 'tabular-nums lining-nums',
                fontSize: typography.fontSize.sm,
                fontWeight: 500,
                color: changeColor,
              }}
            >
              {quote.changesPercentage >= 0 ? '+' : ''}
              {quote.changesPercentage.toFixed(2)}%
            </Typography>
          </Box>

          {/* CTA text */}
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              display: 'block',
              lineHeight: 1.4,
            }}
          >
            Upload your portfolio for personalized risk analysis
          </Typography>
        </Box>
      )}
    </InsightCard>
  );
};

export default RiskInsightCard;
